// src/components/credits/RunRefusalNotice.jsx
//
// The one place a run refusal is rendered — both the pre-flight lock (drawn
// beside the Generate button, from /balance's `block_reason`) and the refusal
// the server returns on POST /pipeline/run.
//
// One component for both because they are the same verdict arriving at two
// moments, and a green pre-flight followed by a 402 is EXPECTED, not a bug: the
// wallet belongs to the customer, every workspace it owns spends from it, and
// /balance is cached 60s. If the two surfaces carried different copy the user
// would read the second one as a new, unexplained failure.
//
// ⚠️ The CTA is not decoration. `NO_PLAN` and `TENANT_RETIRED` are states the
// user cannot fix, so they get "contact your administrator" and NEVER a link to
// a plan picker; `LOOKUP_FAILED` is a 503 the server says is retryable, so it
// gets neither — offering to sell credits for a database blink is a lie.
// `describeRefusal` (utils/runRefusal.js) owns that mapping; this file only
// draws what it is handed.
//
// ⛔ The /billing CTA appears only for users who can buy plans (`useCanBuyPlans`,
// null-safe: outside the AuthProvider it is false, so no link is offered).

import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { describeRefusal } from '../../utils/runRefusal.js';
import { useCanBuyPlans } from '../../auth/canBuyPlans.js';

/**
 * @param {object}  props
 * @param {{code: string, status: number|null, details: object|null}} props.refusal
 * @param {string|null} [props.periodEnd] `period_end` off /balance — only the
 *   exhausted state uses it, and only on a rolling plan.
 * @param {() => void} [props.onDismiss] renders a close button when supplied
 *   (the modal case); omitted for the inline lock, which is not dismissible.
 */
export default function RunRefusalNotice({ refusal, periodEnd, onDismiss }) {
  const canBuyPlans = useCanBuyPlans();   // ⚠️ before the early return (rules of hooks)
  if (!refusal) return null;
  const { title, body, cta } = describeRefusal(refusal, { periodEnd, canBuyPlans });

  return (
    <div
      role="alert"
      style={{
        padding: '12px 14px',
        borderRadius: RADII.control,
        background: SEMANTIC.errore.bg,
        color: SEMANTIC.errore.fg,
        fontFamily: FONT_SANS,
        fontSize: '12px',
        lineHeight: 1.55,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Lock size={14} />
        <strong style={{ fontSize: '13px' }}>{title}</strong>
      </div>
      <p style={{ margin: 0 }}>{body}</p>

      {/* A CTA with no route is advice, not a destination — `contactAdmin` and
          `retry` are rendered as emphasised text so nothing looks clickable
          that cannot be clicked. */}
      <div style={{ marginTop: '10px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {cta.to ? (
          <Link
            to={cta.to}
            style={{ color: SEMANTIC.errore.fg, fontWeight: 700, textDecoration: 'underline' }}
          >
            {cta.label}
          </Link>
        ) : (
          <span style={{ fontWeight: 700 }}>{cta.label}</span>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            style={{
              marginLeft: 'auto',
              padding: '6px 14px',
              borderRadius: RADII.control,
              border: `1px solid ${SEMANTIC.errore.fg}`,
              background: COLORS.surface,
              color: SEMANTIC.errore.fg,
              fontFamily: FONT_SANS,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            aria-label="Chiudi"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
